import React from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortIcon from '@mui/icons-material/Sort';
import { Box, Button, Collapse, LinearProgress, List, Typography } from '@mui/material';

import { useBooleanStateWithToggle } from '@paragon/client-tools';
import { Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';

import { ComponentDesignInProgress, ComponentDesignListItem } from './ComponentDesignListItem';
import { promoteToFrontOfArray } from './promoteToFrontOfArray';

interface Props {
  readonly title: string;
  readonly finished: boolean;

  readonly collapsible?: boolean;
  readonly statusMessage: string;
  readonly componentDesigns: ComponentDesignInProgress[];

  readonly onClose: () => void;
  readonly onCancel?: () => void;
}

export function LongRunningProgressDialog(props: Props) {
  const [isExpanded, toggleExpansion] = useBooleanStateWithToggle(false);
  const [componentsShouldBeSorted, toggleSorting] = useBooleanStateWithToggle(true);

  const totalComponentCount = props.componentDesigns.length;
  const waitingComponentCount = props.componentDesigns.filter((design) => design.category === 'waiting').length;
  const workingComponentCount = props.componentDesigns.filter((design) => design.category === 'working').length;
  const finishedComponentCount = props.componentDesigns.filter((design) => design.category === 'finished').length;

  const shouldShowProgressBar = !props.finished;
  const componentDesignsShouldBeVisible = !props.collapsible || isExpanded;

  let sortedComponentDesigns = props.componentDesigns;
  if (componentsShouldBeSorted) {
    sortedComponentDesigns = promoteToFrontOfArray(
      sortedComponentDesigns,
      (design) => design.problems.some((problem) => problem.type === 'error'),
      (design) => design.problems.some((problem) => problem.type === 'warning'),
    );
  }

  return (
    <Dialog
      insistent
      variant="design"
      contentSx={createScrollbarStyling}
      title={props.title}
      submitText="Close"
      submitDisabled={!props.finished}
      onClose={props.onClose}
      onCancel={props.onCancel}
    >
      {shouldShowProgressBar && (
        <>
          {totalComponentCount === waitingComponentCount || totalComponentCount === finishedComponentCount ? (
            <LinearProgress variant="indeterminate" />
          ) : (
            <LinearProgress
              variant="buffer"
              value={(finishedComponentCount / totalComponentCount) * 100}
              valueBuffer={((finishedComponentCount + workingComponentCount) / totalComponentCount) * 100}
            />
          )}
          <Box sx={{ mb: 1 }} />
        </>
      )}

      <Typography gutterBottom variant={shouldShowProgressBar ? 'body2' : 'body1'} color="textSecondary">
        {props.statusMessage}
      </Typography>

      <Box sx={{ mt: 1 }}>
        {props.collapsible && (
          <Button
            variant="contained"
            size="small"
            startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={toggleExpansion}
          >
            {isExpanded ? 'Fewer' : 'More'} details
          </Button>
        )}

        {componentDesignsShouldBeVisible && (
          <Button
            size="small"
            variant={componentsShouldBeSorted ? 'outlined' : 'contained'}
            sx={{ float: 'right' }}
            startIcon={<SortIcon />}
            onClick={toggleSorting}
          >
            Errors First
          </Button>
        )}
      </Box>
      <Box sx={{ clear: 'both' }} />

      <Collapse in={componentDesignsShouldBeVisible}>
        <List>
          {sortedComponentDesigns.map((design) => (
            <ComponentDesignListItem key={design.name} {...design} />
          ))}
        </List>
      </Collapse>
    </Dialog>
  );
}
